import type { StorefrontPreviewWrite } from './designs.types'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { readProperty } from '@/lib/utils'
import {
  queryOptions,
  useMutation,
  useQuery,
  useSuspenseQuery,
  type InvalidateQueryFilters,
  type Query,
} from '@tanstack/react-query'
import { organizationQueries } from '../organizations/organizations.query'
import { seasonalContentQueryKeys } from '../seasonal-content/seasonal-content.query'
import { storeQueryKeys } from '../stores/stores.query'
import {
  activateDesign,
  createDesignPreviewSession,
  createDesign,
  deleteDesign,
  disableDesignSchedule,
  enableDesignSchedule,
  getDesign,
  getDesigns,
  getAutomaticNavigation,
  getDefaultDesign,
  startStorefrontPreview,
  putStorefrontPreviewSession,
  updateDesign,
  updateDesignScheduleStates,
  updateWebsiteBuilder,
  type DesignIndexQueryKeyEntries,
  type DesignOrganizationScope,
  type DesignShowKeyEntries,
} from './designs.api'
import type {
  CreateDesignInput,
  DesignIdInput,
  DesignInput,
  DesignScheduleStatesInput,
  UpdateWebsiteBuilderInput,
} from './designs.types'

/** Renew a storefront preview session this long after starting it; the storefront keeps one for two hours. */
export const STOREFRONT_PREVIEW_RENEW_MS = 1000 * 60 * 90

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface DesignPreviewSessionScope extends DesignShowKeyEntries {
  previewHost?: string | null | undefined
}

interface StorefrontPreviewScope extends DesignOrganizationScope {
  previewBaseUrl: string | null
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type DesignIndexQueryKey = [AdminQueryRoot & DesignIndexQueryKeyEntries]
type DesignOrganizationQueryKey = [AdminQueryRoot & DesignOrganizationScope]
type DesignShowQueryKey = [AdminQueryRoot & DesignShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

/** Writes to one design name it by `id`, as the design form's values do. */
interface DesignMutationValues {
  id: DesignIdInput
}

export const designQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'design' }, { organizationId }),
  ],
  index: ({ organizationId, filters }: DesignIndexQueryKeyEntries): DesignIndexQueryKey =>
    buildScopedAdminQueryKey(designQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  default: ({ organizationId }: DesignOrganizationScope): DesignOrganizationQueryKey =>
    buildScopedAdminQueryKey(designQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'default',
    }),
  show: ({ organizationId, designId }: DesignShowKeyEntries): DesignShowQueryKey =>
    buildScopedAdminQueryKey(designQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      designId: Number(designId),
    }),
  previewSession: ({
    organizationId,
    designId,
    previewHost = null,
  }: DesignPreviewSessionScope): AdminQueryKey =>
    buildScopedAdminQueryKey(designQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'preview-session',
      designId: designId ?? 'new',
      previewHost: previewHost ?? null,
    }),
  storefrontPreview: ({ organizationId, previewBaseUrl }: StorefrontPreviewScope): AdminQueryKey =>
    buildScopedAdminQueryKey(designQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'storefront-preview',
      previewBaseUrl,
    }),
  automaticNavigation: ({ organizationId, designId }: DesignShowKeyEntries): DesignShowQueryKey =>
    buildScopedAdminQueryKey(designQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'automatic-navigation',
      designId: designId ? Number(designId) : null,
    }),
}

export const designQueries = {
  index: ({ organizationId, filters }: DesignIndexQueryKeyEntries) =>
    queryOptions({
      queryKey: designQueryKeys.index({ organizationId, filters }),
      queryFn: getDesigns,
    }),

  default: ({ organizationId }: DesignOrganizationScope) =>
    queryOptions({
      queryKey: designQueryKeys.default({ organizationId }),
      queryFn: getDefaultDesign,
    }),

  show: ({ organizationId, designId }: DesignShowKeyEntries) =>
    queryOptions({
      queryKey: designQueryKeys.show({ organizationId, designId }),
      queryFn: getDesign,
    }),

  automaticNavigation: ({ organizationId, designId }: DesignShowKeyEntries) =>
    queryOptions({
      queryKey: designQueryKeys.automaticNavigation({ organizationId, designId }),
      queryFn: getAutomaticNavigation,
      staleTime: 1000 * 60,
    }),

  /** A token for read-only `/_preview/<token>/<path>` URLs of the shop's live design. */
  storefrontPreview: ({ organizationId, previewBaseUrl }: StorefrontPreviewScope) =>
    queryOptions({
      queryKey: designQueryKeys.storefrontPreview({ organizationId, previewBaseUrl }),
      queryFn: () =>
        previewBaseUrl
          ? startStorefrontPreview({ organizationId, previewBaseUrl })
          : Promise.reject(new Error('This account has no demo storefront.')),
      // Storefront preview sessions last two hours: renew one before then, on a timer or when the
      // tab comes back, and the frame reloads on the new token.
      staleTime: STOREFRONT_PREVIEW_RENEW_MS,
      refetchInterval: STOREFRONT_PREVIEW_RENEW_MS,
      gcTime: 1000 * 60 * 30,
      retry: 1,
    }),

  previewSession: ({ organizationId, designId, previewHost = null }: DesignPreviewSessionScope) =>
    queryOptions({
      queryKey: designQueryKeys.previewSession({ organizationId, designId, previewHost }),
      queryFn: () => createDesignPreviewSession({ organizationId, designId, previewHost }),
      staleTime: Infinity,
      gcTime: 1000 * 60 * 30,
    }),
}

const isDesignPreviewSessionQuery = (query: Query) =>
  readProperty(query.queryKey[0], 'scope') === 'preview-session'

const isDeletedDesignPreviewSessionQuery = ({
  query,
  designId,
}: {
  query: Query
  designId: DesignIdInput
}) => {
  const [queryKey] = query.queryKey

  return (
    readProperty(queryKey, 'scope') === 'preview-session' &&
    Number(readProperty(queryKey, 'designId')) === Number(designId)
  )
}

export const invalidateDesignQueries = ({
  organizationId,
  refetchType = 'all',
}: OrganizationScope & Pick<InvalidateQueryFilters, 'refetchType'> = {}) =>
  queryClient.invalidateQueries({
    queryKey: designQueryKeys.all({ organizationId }),
    predicate: (query) => !isDesignPreviewSessionQuery(query),
    refetchType,
  })

const invalidateDesignStoreAndOrganizationQueries = (organizationId: number) =>
  Promise.all([
    invalidateDesignQueries({ organizationId }),
    queryClient.invalidateQueries({
      queryKey: storeQueryKeys.all({ organizationId }),
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: organizationQueries.show(organizationId).queryKey,
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: [buildAdminQueryRoot({ entity: 'product' }, { organizationId })],
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: seasonalContentQueryKeys.status({ organizationId }),
      refetchType: 'all',
    }),
  ])

const cleanupDeletedDesignQueries = ({
  organizationId,
  designId,
}: {
  organizationId: number
  designId: DesignIdInput
}) => {
  queryClient.removeQueries({
    queryKey: designQueryKeys.show({ organizationId, designId }),
  })
  queryClient.removeQueries({
    queryKey: designQueryKeys.all({ organizationId }),
    predicate: (query) => isDeletedDesignPreviewSessionQuery({ query, designId }),
  })

  return invalidateDesignStoreAndOrganizationQueries(organizationId)
}

export function useDesigns({
  organizationId,
  filters,
  ...params
}: OrganizationScope &
  Pick<DesignIndexQueryKeyEntries, 'filters'> &
  QueryParams<ReturnType<typeof designQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...designQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useDesign(
  designId: DesignIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof designQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...designQueries.show({ organizationId: resolvedOrganizationId, designId }),
    ...params,
  })
}

export function useDefaultDesign({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof designQueries.default>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...designQueries.default({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useAutomaticNavigation({
  organizationId,
  designId,
  ...params
}: OrganizationScope &
  Pick<DesignShowKeyEntries, 'designId'> &
  QueryParams<ReturnType<typeof designQueries.automaticNavigation>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...designQueries.automaticNavigation({
      organizationId: resolvedOrganizationId,
      designId,
    }),
    enabled: false,
    ...params,
  })
}

export function useDesignPreviewSession({
  organizationId,
  designId,
  previewHost = null,
  enabled = true,
  ...params
}: OrganizationScope &
  Omit<DesignPreviewSessionScope, 'organizationId'> & { enabled?: boolean } & Omit<
    QueryParams<ReturnType<typeof designQueries.previewSession>>,
    'enabled'
  > = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...designQueries.previewSession({
      organizationId: resolvedOrganizationId,
      designId,
      previewHost,
    }),
    enabled: Boolean(enabled && resolvedOrganizationId),
    ...params,
  })
}

export function useCreateDesignMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CreateDesignInput) => createDesign({ organizationId, ...values }),
    onSuccess: () => invalidateDesignStoreAndOrganizationQueries(organizationId),
  })
}

export function useUpdateDesignMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: DesignInput & DesignMutationValues) =>
      updateDesign({ organizationId, ...values }),
    onSuccess: () => invalidateDesignStoreAndOrganizationQueries(organizationId),
  })
}

export function useUpdateWebsiteBuilderMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateWebsiteBuilderInput & DesignMutationValues) =>
      updateWebsiteBuilder({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        invalidateDesignStoreAndOrganizationQueries(organizationId),
        queryClient.invalidateQueries({
          queryKey: [buildAdminQueryRoot({ entity: 'collection' }, { organizationId })],
          refetchType: 'all',
        }),
      ]),
  })
}

export function useDeleteDesignMutation({
  cleanupOnSuccess = true,
}: { cleanupOnSuccess?: boolean } = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedDesign = (designId: DesignIdInput) =>
    cleanupDeletedDesignQueries({ organizationId, designId })
  const mutation = useMutation({
    mutationFn: (designId: DesignIdInput) => deleteDesign({ organizationId, designId }),
    onSuccess: (_, designId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedDesign(designId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedDesign,
  }
}

export function useActivateDesignMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (designId: DesignIdInput) => activateDesign({ organizationId, designId }),
    onSuccess: () => invalidateDesignStoreAndOrganizationQueries(organizationId),
  })
}

export function useDisableDesignScheduleMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (designId: DesignIdInput) => disableDesignSchedule({ organizationId, designId }),
    onSuccess: () => invalidateDesignStoreAndOrganizationQueries(organizationId),
  })
}

export function useEnableDesignScheduleMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (designId: DesignIdInput) => enableDesignSchedule({ organizationId, designId }),
    onSuccess: () => invalidateDesignStoreAndOrganizationQueries(organizationId),
  })
}

export function useUpdateDesignScheduleStatesMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (schedules: DesignScheduleStatesInput) =>
      updateDesignScheduleStates({ organizationId, schedules }),
    onSuccess: () => invalidateDesignStoreAndOrganizationQueries(organizationId),
  })
}

/** Initialize an editor draft, preserving cancellation and the storefront's useful error message. */
export async function initializeStorefrontPreview(input: StorefrontPreviewWrite): Promise<void> {
  const response = await putStorefrontPreviewSession(input)
  if (response.ok) return
  let message = 'Unable to start the storefront preview.'
  try {
    const payload: unknown = await response.json()
    if (
      typeof payload === 'object' &&
      payload !== null &&
      'message' in payload &&
      typeof payload.message === 'string' &&
      payload.message.trim() !== ''
    ) {
      message = payload.message.trim()
    }
  } catch {
    // Keep the fallback message when the response body is not JSON.
  }
  throw new Error(message)
}
